import {GoogleLogin} from "@react-oauth/google";
import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {ConfEnv} from "@/config/config";
import useGoogleLogin from "@/hooks/useGoogleLogin";
import {isNative} from "@/services/nativeBridge.service";
import styles from "./GoogleSignIn.module.css";

// Google Identity Services only accepts a pixel width up to 400; strings like
// "100%" break the rendered button. We measure the container and clamp.
const GOOGLE_BUTTON_MAX_WIDTH = 400;
const GOOGLE_BUTTON_MIN_WIDTH = 200;
const GOOGLE_BUTTON_DEFAULT_WIDTH = 240;

const clampWidth = value => Math.min(GOOGLE_BUTTON_MAX_WIDTH, Math.max(GOOGLE_BUTTON_MIN_WIDTH, Math.floor(value)));

/**
 * Generates a random nonce for the Google OAuth implicit flow.
 */
const generateNonce = () => {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, b => b.toString(16).padStart(2, "0")).join("");
};

/**
 * Builds a Google OAuth redirect URL for the implicit flow.
 * Used inside native WebView where the GIS popup doesn't work.
 * Returns the id_token directly in the URL hash after auth.
 */
const buildGoogleRedirectUrl = () => {
  const params = new URLSearchParams({
    client_id: ConfEnv.GOOGLE_CLIENT_ID,
    redirect_uri: `${window.location.origin}/mobile-login`,
    response_type: "id_token",
    scope: "openid email profile",
    nonce: generateNonce(),
    prompt: "select_account",
  });
  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
};

const GENERIC_ERROR_KEYS = new Set(["networkError", "internalError"]);

const resolveApiErrorMessage = error => {
  const message = error?.message?.trim();
  if (!message || GENERIC_ERROR_KEYS.has(message)) {
    return null;
  }
  return message;
};

/**
 * Google auth button (login and registration share POST /v1/auth/google).
 *
 * @param {{shape?: "rectangular" | "pill", active?: boolean, mode?: "login" | "register"}} props
 * `active` defers mounting the Google iframe until the parent is visible (e.g. modal open).
 * Divider "or" renders above the button in login, below in register.
 */
const GoogleSignIn = ({shape = "rectangular", active = true, mode = "login"}) => {
  const {t} = useTranslation();
  const {postGoogleLogin, isPending, isError, isNewUser, error} = useGoogleLogin();
  const [popupError, setPopupError] = useState(false);
  const containerRef = useRef(null);
  const [buttonWidth, setButtonWidth] = useState(GOOGLE_BUTTON_DEFAULT_WIDTH);

  useEffect(() => {
    if (!active) return;

    const el = containerRef.current;
    if (!el) return;

    const update = () => {
      const measured = el.clientWidth;
      const width = measured > 0 ? measured : GOOGLE_BUTTON_DEFAULT_WIDTH;
      setButtonWidth(clampWidth(width));
    };

    update();

    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(update);
    });

    const observer = new ResizeObserver(update);
    observer.observe(el);

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [active]);

  if (!ConfEnv.GOOGLE_CLIENT_ID) {
    return null;
  }

  const apiErrorMessage = resolveApiErrorMessage(error);

  const errorMessage = popupError
    ? t("googleLoginError")
    : apiErrorMessage
      ? apiErrorMessage
      : isNewUser && mode === "login"
        ? t("googleNewUserRegister")
        : isError || (isNewUser && mode === "register")
          ? t("googleLoginError")
          : null;

  const divider = (
    <div className={styles.divider}>
      <span className={styles.dividerText}>{t("loginOrDivider")}</span>
    </div>
  );

  return (
    <div className={styles.googleSignIn}>
      {mode === "login" && divider}

      <div
        ref={containerRef}
        className={styles.buttonWrapper}
        aria-busy={isPending}
      >
        {active && isNative() ? (
          <button
            type="button"
            className={styles.nativeGoogleButton}
            onClick={() => {
              window.location.href = buildGoogleRedirectUrl();
            }}
          >
            <svg
              viewBox="0 0 48 48"
              width="20"
              height="20"
              role="img"
              aria-label="Google"
            >
              <title>Google</title>
              <path
                fill="#EA4335"
                d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
              />
              <path
                fill="#4285F4"
                d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
              />
              <path
                fill="#FBBC05"
                d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
              />
              <path
                fill="#34A853"
                d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
              />
            </svg>
            <span>{t("googleContinueWith") || "Continuar con Google"}</span>
          </button>
        ) : (
          active && (
            <GoogleLogin
              key={buttonWidth}
              onSuccess={credentialResponse => {
                setPopupError(false);
                if (credentialResponse.credential) {
                  postGoogleLogin({idToken: credentialResponse.credential});
                }
              }}
              onError={() => setPopupError(true)}
              size="large"
              width={buttonWidth}
              text="continue_with"
              shape={shape}
              theme="outline"
              locale="es"
            />
          )
        )}
      </div>

      {mode === "register" && divider}

      {errorMessage && (
        <p
          className={styles.error}
          role="alert"
        >
          {errorMessage}
        </p>
      )}
    </div>
  );
};

export default GoogleSignIn;
